/**
 * Presentational helpers for interactions board/table.
 * Дата: 17.07.2026
 */
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import type { InteractionEvent } from '@/types/interaction';

export function isOkdocShortlinkSource(channel: string) {
  return channel.endsWith('_shortlink');
}

export function readInteractionMetadataString(event: InteractionEvent, key: string): string {
  const value = event.metadata?.[key];
  return typeof value === 'string' ? value.trim() : '';
}

export function getInteractionPatientDisplayName(event: InteractionEvent): string {
  return (
    readInteractionMetadataString(event, 'patient_fio') ||
    readInteractionMetadataString(event, 'patient_name_full') ||
    (event.patient_name || '').trim()
  );
}

export function getInteractionAppointmentLabel(event: InteractionEvent): string | null {
  const raw = readInteractionMetadataString(event, 'appointment_datetime');
  if (!raw) return null;

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return format(parsed, 'dd.MM.yyyy HH:mm', { locale: ru });
  }

  return raw;
}

export function getInteractionFlowLabel(event: InteractionEvent): string | null {
  if (
    readInteractionMetadataString(event, 'scenario_kind') === 'birthday_offer' ||
    event.event_type === 'gift_choice'
  ) {
    return 'ДР';
  }
  const flow = readInteractionMetadataString(event, 'interaction_flow');
  if (flow === 'appointment_confirmation') return 'Подтверждение записи';
  if (flow === 'feedback_collection' || event.event_type.startsWith('feedback_')) return 'Сбор ОС';
  if (
    flow === 'contact_hub' ||
    readInteractionMetadataString(event, 'interaction_intent') === 'contact_navigation' ||
    event.event_type === 'contact_navigation_open'
  ) {
    return 'Каналы связи';
  }
  if (readInteractionMetadataString(event, 'scenario_family') === 'marketing') return 'Маркетинг';
  if (flow === 'post_procedure' || event.event_type === 'cluster_response') return 'После процедур';
  return null;
}

export function getInteractionScenarioLabel(event: InteractionEvent): string | null {
  const explicitBadge = readInteractionMetadataString(event, 'scenario_badge');
  if (explicitBadge) return explicitBadge;

  const variant = readInteractionMetadataString(event, 'scenario_variant');
  if (variant === 'post_visit_promo_ulybkapro_pediatric_v1') return 'Детский прием';
  if (variant === 'post_visit_promo_dhc_checkup_v1') return 'Бесплатный чекап';

  const title = readInteractionMetadataString(event, 'scenario_title');
  return title || null;
}

export function getInteractionStatusMeta(status: InteractionEvent['status']): {
  label: string;
  className: string;
} {
  switch (status) {
    case 'new':
      return {
        label: 'Новый',
        className: 'border-shell-primary/25 bg-shell-primary-soft text-shell-primary',
      };
    case 'processed':
      return {
        label: 'Обработан',
        className: 'border-shell-success/25 bg-shell-success-soft text-shell-success',
      };
    default:
      return {
        label: 'CRM',
        className: 'border-shell-border bg-shell-elevated text-shell-muted',
      };
  }
}
